/**
 * Upload template specifications.
 *
 * Single source of truth for the three analyst-facing files. The server generates
 * the blank .xlsx/.csv from this, validates incoming files against it, and the
 * admin UI renders the expected-columns help from it. Change a column here and
 * the template, the validator and the on-screen instructions all move together.
 *
 * Column order deliberately mirrors the order the values appear in the existing
 * `NAV-SFI-*.xlsx` workbook, so a block can be copy-pasted with minimal reshaping.
 */
const FUND_CODE_COLUMN = {
    header: 'fund_code',
    type: 'string',
    required: true,
    example: 'SFFR',
    description: 'Fund short code. Must already exist in the dashboard (SFFR, SFFD, SFIS).',
};
export const NAV_DAILY_TEMPLATE = {
    dataset: 'nav_daily',
    filename: 'nav_daily_template',
    title: 'Daily NAV',
    purpose: 'One row per fund per business day. Drives the NAV Harian table and the freshness stamp.',
    primaryKey: ['fund_code', 'date'],
    columns: [
        FUND_CODE_COLUMN,
        {
            header: 'date',
            type: 'date',
            required: true,
            example: '2026-06-30',
            description: 'Valuation date in YYYY-MM-DD. Excel real dates are accepted too.',
        },
        {
            header: 'nav_value',
            type: 'number',
            required: true,
            example: '1263.0786',
            description: 'NAV per unit in the fund currency, unrounded. IDR ~1000-2000, USD ~1.0-1.3. CSV uses a comma decimal (1263,0786) so Excel does not read it as millions; a dot (1263.0786) still uploads.',
        },
    ],
    rules: [
        'One row per fund per date — a repeated (fund_code, date) inside one file is rejected.',
        'Re-uploading a date that already exists overwrites its value; nothing else is touched.',
        'Dates in the future relative to today produce a warning, not an error.',
        'A NAV that moves more than 10% from the previous day produces a warning to catch typos.',
        'CSV uses semicolon (;) between columns and a comma decimal (1263,0786) so Indonesian Excel keeps NAV in the thousands. Older comma-delimited files and dot decimals still upload.',
    ],
};
export const NAV_MONTHLY_TEMPLATE = {
    dataset: 'nav_monthly',
    filename: 'nav_monthly_template',
    title: 'Monthly NAV',
    purpose: 'One row per fund per month — the month-end NAV. Drives the chart, the Data NAV Bulanan table and every return metric.',
    primaryKey: ['fund_code', 'period'],
    columns: [
        FUND_CODE_COLUMN,
        {
            header: 'period',
            type: 'period',
            required: true,
            example: '2026-06',
            description: 'Month in YYYY-MM. This is the month the NAV closes, not the reporting date.',
        },
        {
            header: 'nav_close',
            type: 'number',
            required: true,
            example: '1263.0786',
            description: 'Month-end NAV per unit ("NAV Akhir Bulan" in the workbook), unrounded. CSV uses a comma decimal so Excel keeps the value in the thousands.',
        },
        {
            header: 'aum',
            type: 'number',
            required: false,
            example: '245.8',
            description: 'Assets under management in MILLIONS of the fund currency. Leave blank if unknown — the tile shows a dash.',
        },
    ],
    rules: [
        'MoM and YoY are NOT uploaded — the dashboard computes them from nav_close so they can never disagree with the NAV.',
        'Leaving a month out is fine; the chart simply skips it and YoY for the following year reports as unknown.',
        'A month-end NAV that contradicts the last daily NAV of the same month produces a warning.',
        'CSV uses semicolon (;) between columns and a comma decimal (1263,0786) so Indonesian Excel keeps NAV in the thousands. Older comma-delimited files and dot decimals still upload.',
    ],
};
export const HOLDINGS_TEMPLATE = {
    dataset: 'holdings',
    filename: 'holdings_template',
    title: 'Portfolio Holdings',
    purpose: 'Top securities per fund per month, in weight order. Drives the Portfolio Holdings card.',
    primaryKey: ['fund_code', 'period', 'rank'],
    columns: [
        FUND_CODE_COLUMN,
        {
            header: 'period',
            type: 'period',
            required: true,
            example: '2026-06',
            description: 'Month the holding snapshot belongs to, YYYY-MM.',
        },
        {
            header: 'rank',
            type: 'integer',
            required: true,
            example: '1',
            description: 'Position by weight, starting at 1. Must be unique within a fund + period.',
        },
        {
            header: 'security_name',
            type: 'string',
            required: true,
            example: 'OBL BKLT IV Bank Victoria Tahap II Tahun 2025',
            description: 'Full instrument name exactly as published by the Investment team.',
        },
        {
            header: 'security_type',
            type: 'string',
            required: false,
            example: 'Obligasi Korporasi',
            description: 'Instrument class, e.g. Obligasi Korporasi, Obligasi Negara, Deposito, Saham.',
        },
        {
            header: 'sector',
            type: 'string',
            required: false,
            example: 'Perbankan',
            description: 'Issuer sector. Drives the coloured badge; unknown sectors get a neutral grey.',
        },
        {
            header: 'pct_aum',
            type: 'number',
            required: false,
            example: '14.5',
            description: 'Share of fund AUM in PERCENT (14.5 means 14.5%, not 0.145).',
        },
        {
            header: 'rating',
            type: 'string',
            required: false,
            example: 'idBBB+',
            description: 'Credit rating as published, e.g. idAAA, Baa2. Leave blank for equities.',
        },
    ],
    rules: [
        'Uploading a (fund_code, period) REPLACES that month\'s holdings entirely — this is how a security drops out of the top 10.',
        'Ranks should be contiguous from 1; a gap produces a warning.',
        'If pct_aum is supplied for every row and the total exceeds 100%, the file is rejected.',
        'CSV uses semicolon (;) between columns and a comma decimal (1263,0786) so Indonesian Excel keeps NAV in the thousands. Older comma-delimited files and dot decimals still upload.',
    ],
};
export const TEMPLATE_SPECS = {
    nav_daily: NAV_DAILY_TEMPLATE,
    nav_monthly: NAV_MONTHLY_TEMPLATE,
    holdings: HOLDINGS_TEMPLATE,
};
export const TEMPLATE_LIST = [
    NAV_DAILY_TEMPLATE,
    NAV_MONTHLY_TEMPLATE,
    HOLDINGS_TEMPLATE,
];
/** File extensions the upload endpoint accepts. */
export const ACCEPTED_UPLOAD_EXTENSIONS = ['.csv', '.xlsx'];
//# sourceMappingURL=templates.js.map